import React, { useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { getCookie, setCookie } from "../../untils/cookies/index";
import { useActions } from "../../hooks/actions";
import { getCurrentLanguage } from "../../store/directories/selectors";
import { getUserRole } from "../../store/user/selectors";
import { TranslationObject } from "../../models/translationModal";
import { runTrackingAfterConsentInject } from "./trackingAfterConsent";

declare global {
    interface Window {
        __ENFORCE_TRACKING_COOKIE_CONSENT__?: boolean | string;
        __SSR_LOADED_CONSENT_TRACKING__?: boolean;
    }
}

interface Props {
    entities?: TranslationObject;
    isFirefox?: boolean;
}

const COOKIE_CONSENT_KEY = "agreeWithCookie";

const CookieBlock: React.FC<Props> = ({ entities, isFirefox }) => {
    const container = document.getElementById("root") || document.body;
    const [isOpen, setIsOpen] = useState(false);
    const currentLanguage = getCurrentLanguage();
    const { updateAgreeWithCookie } = useActions();
    const userRole = getUserRole();
    const location = useLocation();

    const onChangeCookie = useCallback(
        async (isFullConsent: boolean) => {
            setCookie(COOKIE_CONSENT_KEY, String(isFullConsent));
            updateAgreeWithCookie(isFullConsent);
            setIsOpen(false);

            if (!isFullConsent) {
                return;
            }

            await runTrackingAfterConsentInject();
        },
        [updateAgreeWithCookie]
    );

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const queryDisableCookies = queryParams.get("disable_cookies");

        if (queryDisableCookies === "true") {
            void onChangeCookie(false);
            queryParams.delete("disable_cookies");
            return;
        }

        const cookieValue = getCookie(COOKIE_CONSENT_KEY);

        if (cookieValue !== undefined) {
            const isFullConsent = cookieValue === "true";
            updateAgreeWithCookie(isFullConsent);
            if (isFullConsent) {
                void runTrackingAfterConsentInject();
            }
            return;
        }
        setIsOpen(true);
    }, [location.search, onChangeCookie, updateAgreeWithCookie]);

    const url =
        userRole !== "guest"
            ? `${currentLanguage}/${userRole}/cookies-policy`
            : `${currentLanguage}/cookies-policy`;

    const jsxCookies = () => (
        <div className="cookies cookies--modal">
            <div className="cookies__container">
                <p className="cookies__text">
                    {entities?.cookies_text?.value}&nbsp;
                    <Link to={url} className="link">
                        {entities?.cookies_link_value?.value}
                    </Link>
                </p>
                <button
                    className="btn btn--rounded cookies__btn--all cookies__btn"
                    type="button"
                    onClick={() => onChangeCookie(true)}
                >
                    {entities?.cookies_button_agree_value?.value}
                </button>
                {entities?.cookies_button_agree_some_value?.value && (
                    <button
                        className="btn btn--rounded cookies__btn"
                        type="button"
                        onClick={() => onChangeCookie(false)}
                    >
                        {entities.cookies_button_agree_some_value.value}
                    </button>
                )}
            </div>
        </div>
    );

    return isOpen
        ? isFirefox
            ? ReactDOM.createPortal(jsxCookies(), container)
            : jsxCookies()
        : null;
};

export default CookieBlock;
